import { PortalBadge, portalLabel } from "@/components/PortalBadge";
import type { ChannelPortalStatus, PortalDataState } from "./portal-status";

const RING: Record<ChannelPortalStatus["state"], string> = {
  active: "ring-1 ring-success/60",
  inactive: "ring-1 ring-coral/70",
  excluded: "ring-1 ring-line",
};

/**
 * Symbol je Zustand — bewusst unterschiedlich statt nur per Ringfarbe (WCAG 1.4.1,
 * Spec §6): Zustand darf nicht ausschliesslich ueber Farbe erkennbar sein.
 * `excluded` traegt stattdessen ein Wort (siehe VISIBLE_LABEL).
 */
const ICON: Partial<Record<ChannelPortalStatus["state"], string>> = {
  active: "↗",
  inactive: "✕",
};

/**
 * Zustaende, die ein sichtbares Wort statt eines Symbols tragen. „–" allein sagt
 * dem Operator nicht, was gemeint ist; Spec §6 verlangt fuer `excluded`
 * ausdruecklich ein graues Badge mit sichtbarem Label „ausgeschlossen". Vorher
 * stand das Wort nur im title- und aria-label — also fuer sehende Nutzer erst
 * nach Hovern. Der Zustand bleibt grau: er ist kein Fehler, sondern eine bewusste
 * Entscheidung im Abgleich, und darf nie rot aussehen.
 */
const VISIBLE_LABEL: Partial<Record<ChannelPortalStatus["state"], string>> = {
  excluded: "ausgeschlossen",
};

/**
 * Formatiert ein ISO-Datum als DD.MM.JJJJ in der Zeitzone Europe/Berlin — Projekt-
 * Konvention (CLAUDE.md "Konventionen": Zeitstempel immer Europe/Berlin, nie UTC).
 * Gleiches Muster wie fmtDatum in VerkaufslinksTab.tsx fuer dasselbe Feld
 * (inactiveSince). Ein rein UTC-basiertes Parsing zeigt fuer Zeitstempel zwischen
 * Mitternacht und ca. 02:00 Berliner Zeit systematisch den Vortag. Liefert null bei
 * ungueltigem/fehlendem Datum, statt "Invalid Date" durchzureichen.
 */
function formatDateDE(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Berlin",
  });
}

/** Zustand in einem Satz — ohne die Liste der weiteren Angebote. */
function headline(s: ChannelPortalStatus): string {
  if (s.state === "active") {
    return s.count > 1 ? `${s.count} aktive Angebote — Link zum ersten` : "Angebot aktiv";
  }
  if (s.state === "inactive") {
    const seit = s.inactiveSince ? formatDateDE(s.inactiveSince) : null;
    return `offline seit ${seit ?? "unbekannt"}`;
  }
  return "vom Abgleich ausgeschlossen";
}

/**
 * Tooltip = Zustand + die uebrigen Angebote desselben Portals (Spec §6: „die
 * uebrigen erscheinen im Tooltip"). Die blosse Anzahl liess Konto und URL der
 * weiteren Angebote verschwinden — der Operator sah, DASS es sie gibt, kam aber
 * nicht hin. Mehrzeilig, weil \n im title-Attribut umbricht und drei URLs in
 * einer Zeile unlesbar waeren.
 */
function tooltip(s: ChannelPortalStatus): string {
  if (s.others.length === 0) return headline(s);
  const weitere = s.others.map((o) => `• ${o.accountKey} — ${o.url}`).join("\n");
  return `${headline(s)}\nWeitere Angebote:\n${weitere}`;
}

/**
 * Fuer Screenreader: Portal + Zustand als vollstaendiger, farbunabhaengiger Text.
 * Der Portalname kommt aus derselben Quelle wie das sichtbare Badge (portalLabel),
 * sonst laufen Gesehenes und Vorgelesenes auseinander — „eBay" vs. „Ebay", und bei
 * Shopify sogar inhaltlich: sichtbar „Webshop", vorgelesen „Shopify".
 *
 * Die weiteren Angebote werden nur gezaehlt statt aufgelistet: vorgelesene URLs
 * sind Laerm, die Information „es gibt weitere" bleibt aber erhalten.
 */
function accessibleLabel(s: ChannelPortalStatus): string {
  const n = s.others.length;
  const weitere =
    n === 0 ? "" : n === 1 ? " — 1 weiteres Angebot" : ` — ${n} weitere Angebote`;
  return `${portalLabel(s.portal)}: ${headline(s)}${weitere}`;
}

/** Leer-Zustaende: Text + Tooltip. Alle grau — nie rot (siehe Komponenten-Doku). */
const EMPTY: Record<PortalDataState, { text: string; title: string }> = {
  ok: {
    text: "— keine Daten",
    title: "Kein Eintrag im Verkaufslinks-Abgleich — nicht zwingend offline",
  },
  loading: {
    text: "— lädt…",
    title: "Portal-Angebote werden geladen",
  },
  error: {
    text: "— Status nicht abrufbar",
    title:
      "Verkaufslinks-Abgleich nicht erreichbar — kein Hinweis auf den Zustand der Listung",
  },
};

/**
 * Portal-Kennung je Kanal-Zeile. Leere Liste = kein Zustandsbadge — bewusst NICHT
 * rot, weil ein fehlender Eintrag kein Beleg fuer „offline" ist (Konto-/Sync-Ausfall).
 * `dataState` unterscheidet dabei „es gibt wirklich keinen Eintrag" von „es wurde
 * nie nachgesehen"; beide bleiben grau, nur der Text wird ehrlich.
 */
export function PortalStatusBadges({
  statuses,
  dataState = "ok",
}: {
  statuses: ChannelPortalStatus[];
  dataState?: PortalDataState;
}) {
  if (statuses.length === 0) {
    const { text, title } = EMPTY[dataState];
    return (
      <span className="text-[10px] text-muted-2 font-mono" title={title}>
        {text}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      {statuses.map((s) => (
        <a
          key={s.portal}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          title={tooltip(s)}
          aria-label={accessibleLabel(s)}
          className={`inline-flex items-center gap-0.5 rounded ${RING[s.state]} hover:opacity-80`}
        >
          <PortalBadge portal={s.portal} />
          {s.count > 1 && <span className="text-[10px] font-mono text-ink-2">{s.count}</span>}
          {VISIBLE_LABEL[s.state] ? (
            <span className="text-[10px] font-mono text-ink-2">{VISIBLE_LABEL[s.state]}</span>
          ) : (
            <span aria-hidden className="text-[10px] text-muted-2">{ICON[s.state]}</span>
          )}
        </a>
      ))}
    </span>
  );
}
